import { AnimatePresence, motion } from "framer-motion";
import { useAtom, useSetAtom } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import type { InferGetServerSidePropsType, NextPage } from "next";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";

import { CardCategoryEnum } from "@sorcery/db";
import { LoadingSpinner } from "@sorcery/ui";

import { Page } from "~/components/layouts/Page";
import { FeatureSplashImage } from "~/components/molecules/splashes/FeatureSplashImage";
import { useUser } from "~/providers/UserProvider";
import {
  avatarJotai,
  deckJotai,
  deckResetterJotai,
  decklistJotai,
  maybeboardJotai,
  sideboardJotai,
} from "~/state/deckJotai";
import { DeckModeEnum } from "~/types/deck";
import { ogPath } from "~/utils/paths";
import { fadeInOutTransition, fadeInOutVariants } from "~/utils/transitions";
import { api, jotaiStateDelegationOptions, redirectableOptions } from "~/utils/trpc";
import { DeckHeader } from "./DeckHeader";
import { DeckSplash } from "./DeckSplash";
import type { getServerSideProps } from "./index.page";
import { EditTab } from "./tabs/Edit/EditTab";
import { PrimerTab } from "./tabs/Primer/PrimerTab";
import { Settings } from "./tabs/Settings/SettingsTab";
import { ViewTab } from "./tabs/View/ViewTab";

const Deck: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = (props) => {
  /************************************************************************************
   * initialization
   */
  // available instantly due to SSR
  const { deckId } = props;
  const { user, isLoading: userLoading } = useUser();
  const { query: routerParams, isReady } = useRouter();
  const tab = routerParams.tab as DeckModeEnum | undefined;
  const [activeTab, setActiveTab] = useState<DeckModeEnum>(DeckModeEnum.VIEW);
  const resetJotai = useSetAtom(deckResetterJotai);

  /************************************************************************************
   * data fetching + jotai state hydration
   */
  useHydrateAtoms([[deckJotai, api.useUtils().deck.getById.getData({ id: deckId })]]);
  const [deck, setDeck] = useAtom(deckJotai);
  // available instantly due to SSG helpers
  api.deck.getById.useQuery(
    { id: deckId },
    {
      ...jotaiStateDelegationOptions,
      ...redirectableOptions,
      select: (data) => {
        setDeck(data);
        return data;
      },
    },
  );
  const setDecklist = useSetAtom(decklistJotai);
  const { isPending: decklistPending } = api.deck.getDecklistById.useQuery(
    { id: deckId },
    {
      ...jotaiStateDelegationOptions,
      ...redirectableOptions,
      select: (data) => {
        setDecklist(data);
        return data;
      },
    },
  );
  const setAvatar = useSetAtom(avatarJotai);
  const { isPending: avatarPending } = api.deck.getAvatarById.useQuery(
    { id: deckId },
    {
      ...jotaiStateDelegationOptions,
      ...redirectableOptions,
      select: (data) => {
        setAvatar(data);
        return data;
      },
    },
  );
  const setSideboard = useSetAtom(sideboardJotai);
  const { isPending: sideboardPending } = api.deck.getSideboardById.useQuery(
    { id: deckId },
    {
      ...jotaiStateDelegationOptions,
      ...redirectableOptions,
      select: (data) => {
        setSideboard(data);
        return data;
      },
    },
  );
  const setMaybeboard = useSetAtom(maybeboardJotai);
  const { isPending: maybeboardPending } = api.deck.getMaybeboardById.useQuery(
    { id: deckId },
    {
      ...jotaiStateDelegationOptions,
      ...redirectableOptions,
      select: (data) => {
        setMaybeboard(data);
        return data;
      },
    },
  );

  const { mutateAsync: tickUpViews } = api.deck.viewDeck.useMutation(redirectableOptions);

  /*************************************************************************************************************************
   * react helpers
   */
  const isLoading = useMemo(
    () => userLoading || decklistPending || avatarPending || sideboardPending || maybeboardPending,
    [userLoading, decklistPending, avatarPending, sideboardPending, maybeboardPending],
  );
  const isAuthor = useMemo(() => !!user && !!deck && user.id === deck.user.id, [deck, user]);

  const showViewTab = activeTab === DeckModeEnum.VIEW;
  const showPrimerTab = activeTab === DeckModeEnum.PRIMER;
  const showEditTab = activeTab === DeckModeEnum.EDIT && isAuthor;
  const showSettingsTab = activeTab === DeckModeEnum.SETTINGS && isAuthor;

  /*************************************************************************************************************************
   * side effects
   */
  // Track tab when router is loaded
  useEffect(() => {
    if (isReady) {
      if (tab && Object.values(DeckModeEnum).includes(tab)) {
        setActiveTab(tab);
      } else {
        setActiveTab(DeckModeEnum.VIEW);
      }
    }
  }, [tab, isReady]);

  useEffect(() => {
    const t = setTimeout(() => {
      void tickUpViews({ id: deckId });
    }, 5000);
    return () => clearTimeout(t);
  }, [deckId, tickUpViews]);

  //  clear deck jotais on unmount
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    return () => {
      resetJotai();
    };
  }, []);

  /*************************************************************************************************************************
   * render
   */
  if (!deck) return null;

  return (
    <>
      {deck.feature && (
        <FeatureSplashImage
          src={deck.feature.src}
          rotate={deck.feature.card.guardian?.category === CardCategoryEnum.Site}
        />
      )}

      <Page
        title={`${deck.name} | by @${deck.user.username}.`}
        description={`A ${deck.format} ${
          deck.avatars[0]?.card.name ?? "Sorcery"
        } deck created on Curiosa.`}
        image={ogPath(
          deck.feature.src,
          deck.feature.card.guardian?.category === CardCategoryEnum.Site ? 1 : 0,
        )}
      >
        {/* ssr'd skeleton details while full deck data loads */}
        <DeckSplash />

        {/* full deck data */}
        {isLoading ? (
          <div className="flex h-[50vh] w-full items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {/* Header */}
            <DeckHeader tab={activeTab} />
            {/* Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                variants={fadeInOutVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={fadeInOutTransition}
              >
                {showViewTab && <ViewTab isAuthor={isAuthor} />}
                {showPrimerTab && <PrimerTab isAuthor={isAuthor} />}
                {showEditTab && <EditTab />}
                {showSettingsTab && <Settings />}
              </motion.div>
            </AnimatePresence>
          </>
        )}
      </Page>
    </>
  );
};

export default Deck;
